// Contact1.js - Contains functions for creating a contact section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Contact section with a form and contact information
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the contact section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The contact section elements
 */
export const createContactElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const contactSectionClass = 'contact1-section';
  const contactContainerClass = 'contact1-container';
  const contactHeaderClass = 'contact1-header';
  const contactTitleClass = 'contact1-title';
  const contactSubtitleClass = 'contact1-subtitle';
  const contactContentClass = 'contact1-content';
  const contactInfoClass = 'contact1-info';
  const contactInfoItemClass = 'contact1-info-item';
  const contactInfoIconClass = 'contact1-info-icon';
  const contactInfoTextClass = 'contact1-info-text';
  const contactFormWrapperClass = 'contact1-form-wrapper';
  const contactFormClass = 'contact1-form';
  const contactFormGroupClass = 'contact1-form-group';
  const contactFormLabelClass = 'contact1-form-label';
  const contactFormInputClass = 'contact1-form-input';
  const contactFormTextareaClass = 'contact1-form-textarea';
  const contactFormButtonClass = 'contact1-form-button';
  const contactFormSuccessClass = 'contact1-form-success';
  const contactFormErrorClass = 'contact1-form-error';
  
  // Desktop Styles (default)
  const contactSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8f9fa',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const contactContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const contactHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const contactTitleStyles = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '16px'
  };

  const contactSubtitleStyles = {
    fontSize: '18px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '30px'
  };

  const contactContentStyles = {
    display: 'flex',
    width: '100%',
    gap: '40px',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  };

  const contactInfoStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    flex: '1',
    maxWidth: '400px'
  };

  const contactInfoItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  };

  const contactInfoIconStyles = {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    backgroundColor: '#4361ee',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#ffffff',
    fontSize: '18px',
    padding: '5px'
  };

  const contactInfoTextStyles = {
    fontSize: '16px',
    color: '#666666'
  };

  const contactFormWrapperStyles = {
    flex: '1',
    maxWidth: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
    padding: '40px'
  };

  const contactFormStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%'
  };

  const contactFormGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const contactFormLabelStyles = {
    fontSize: '16px',
    fontWeight: '500',
    color: '#333333'
  };

  const contactFormInputStyles = {
    padding: '12px 15px',
    borderRadius: '5px',
    border: '1px solid #e1e1e1',
    fontSize: '16px',
    color: '#333333',
    backgroundColor: '#f9f9f9'
  };

  const contactFormTextareaStyles = {
    padding: '12px 15px',
    borderRadius: '5px',
    border: '1px solid #e1e1e1',
    fontSize: '16px',
    color: '#333333',
    backgroundColor: '#f9f9f9',
    minHeight: '120px',
    resize: 'vertical'
  };

  const contactFormButtonStyles = {
    padding: '14px 24px',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: '500',
    backgroundColor: '#4361ee',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    marginTop: '10px',
    transition: 'background-color 0.3s ease'
  };

  const contactFormSuccessStyles = {
    padding: '15px',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '5px',
    marginTop: '20px',
    display: 'none'
  };

  const contactFormErrorStyles = {
    padding: '15px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '5px',
    marginTop: '20px',
    display: 'none'
  };
  
  // Tablet Styles
  const tabletContactContentStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '30px'
  };
  
  const tabletContactInfoStyles = {
    maxWidth: '100%'
  };
  
  const tabletContactFormWrapperStyles = {
    maxWidth: '100%',
    width: '100%'
  };
  
  // Mobile Styles
  const mobileContactHeaderStyles = {
    marginBottom: '30px'
  };
  
  const mobileContactTitleStyles = {
    fontSize: '28px'
  };
  
  const mobileContactSubtitleStyles = {
    fontSize: '16px'
  };
  
  const mobileContactFormWrapperStyles = {
    padding: '30px 20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(contactSectionClass, contactSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(contactContainerClass, contactContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(contactHeaderClass, contactHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(contactTitleClass, contactTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSubtitleClass, contactSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactContentClass, contactContentStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoClass, contactInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoItemClass, contactInfoItemStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoIconClass, contactInfoIconStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoTextClass, contactInfoTextStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormWrapperClass, contactFormWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormClass, contactFormStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormGroupClass, contactFormGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormLabelClass, contactFormLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormInputClass, contactFormInputStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormTextareaClass, contactFormTextareaStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormButtonClass, contactFormButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormSuccessClass, contactFormSuccessStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormErrorClass, contactFormErrorStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(contactContentClass, tabletContactContentStyles);
    newRegistry[DEVICES.TABLET].set(contactInfoClass, tabletContactInfoStyles);
    newRegistry[DEVICES.TABLET].set(contactFormWrapperClass, tabletContactFormWrapperStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(contactHeaderClass, mobileContactHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(contactTitleClass, mobileContactTitleStyles);
    newRegistry[DEVICES.MOBILE].set(contactSubtitleClass, mobileContactSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(contactFormWrapperClass, mobileContactFormWrapperStyles);
    
    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    contactSectionClass,
    contactContainerClass,
    contactHeaderClass,
    contactTitleClass,
    contactSubtitleClass,
    contactContentClass,
    contactInfoClass,
    contactInfoItemClass,
    contactInfoIconClass,
    contactInfoTextClass,
    contactFormWrapperClass,
    contactFormClass,
    contactFormGroupClass,
    contactFormLabelClass,
    contactFormInputClass,
    contactFormTextareaClass,
    contactFormButtonClass,
    contactFormSuccessClass,
    contactFormErrorClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  
  // Header elements
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  
  // Content container
  const contentId = Date.now() + 5;
  
  // Contact information elements
  const infoContainerId = Date.now() + 6;
  
  // Address info
  const addressItemId = Date.now() + 7;
  const addressIconId = Date.now() + 8;
  const addressTextId = Date.now() + 9;
  const addressTextLine2Id = Date.now() + 100;
  
  // Phone info
  const phoneItemId = Date.now() + 10;
  const phoneIconId = Date.now() + 11;
  const phoneTextId = Date.now() + 12;
  const phoneTextLine2Id = Date.now() + 101;
  
  // Email info
  const emailItemId = Date.now() + 13;
  const emailIconId = Date.now() + 14;
  const emailTextId = Date.now() + 15;
  const emailTextLine2Id = Date.now() + 102;
  
  // Form elements
  const formWrapperId = Date.now() + 16;
  const formId = Date.now() + 17;
  
  // Name field
  const nameGroupId = Date.now() + 18;
  const nameLabelId = Date.now() + 19;
  const nameInputId = Date.now() + 20;
  
  // Email field
  const emailGroupId = Date.now() + 21;
  const emailLabelId = Date.now() + 22;
  const emailInputId = Date.now() + 23;
  
  // Message field
  const messageGroupId = Date.now() + 24;
  const messageLabelId = Date.now() + 25;
  const messageTextareaId = Date.now() + 26;
  
  // Submit button and status messages
  const submitButtonId = Date.now() + 27;
  const successMessageId = Date.now() + 28;
  const errorMessageId = Date.now() + 29;
  
  const newElements = [
    // Main Contact Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Contact Section',
      parentId: null,
      className: contactSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'contact-section',
        'data-component': 'section',
        'data-section-type': 'contact',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: contactContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'contact-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contactHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'contact-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Contact Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: contactTitleClass,
      styles: {},
      children: [],
      text: 'Contact Us',
      attributes: {
        'data-element': 'contact-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Contact Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: contactSubtitleClass,
      styles: {},
      children: [],
      text: 'Have questions or need help? Get in touch with our team and we\'ll be happy to assist you.',
      attributes: {
        'data-element': 'contact-subtitle'
      }
    },
    
    // Content Container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contactContentClass,
      styles: {},
      children: [infoContainerId, formWrapperId],
      text: '',
      attributes: {
        'data-element': 'contact-content'
      }
    },
    
    // Contact Info Container
    {
      id: infoContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Info',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoClass,
      styles: {},
      children: [addressItemId, phoneItemId, emailItemId],
      text: '',
      attributes: {
        'data-element': 'contact-info'
      }
    },
    
    // Address Item
    {
      id: addressItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoItemClass,
      styles: {},
      children: [addressIconId, addressTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },
    
    // Address Icon
    {
      id: addressIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Address Icon',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('image'),
      className: contactInfoIconClass,
      styles: {},
      src: 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6/svgs/solid/location-dot.svg',
      attributes: {
        'data-element': 'contact-info-icon',
        'alt': 'Location Icon'
      }
    },
    
    // Address Text Line 1
    {
      id: addressTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Text Line 1',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {},
      children: [addressTextLine2Id],
      text: '123 Business Street, Suite 100',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Address Text Line 2
    {
      id: addressTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Text Line 2',
      parentId: addressTextId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {},
      children: [],
      text: 'New York, NY 10001',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Phone Item
    {
      id: phoneItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoItemClass,
      styles: {},
      children: [phoneIconId, phoneTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },
    
    // Phone Icon
    {
      id: phoneIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Phone Icon',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('image'),
      className: contactInfoIconClass,
      styles: {},
      src: 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6/svgs/solid/phone.svg',
      attributes: {
        'data-element': 'contact-info-icon',
        'alt': 'Phone Icon'
      }
    },
    
    // Phone Text Line 1
    {
      id: phoneTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Text Line 1',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {},
      children: [phoneTextLine2Id],
      text: '+1 (555) 123-4567',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Phone Text Line 2
    {
      id: phoneTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Text Line 2',
      parentId: phoneTextId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {},
      children: [],
      text: 'Monday-Friday, 9am-5pm EST',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Email Item
    {
      id: emailItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoItemClass,
      styles: {},
      children: [emailIconId, emailTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },
    
    // Email Icon
    {
      id: emailIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Email Icon',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('image'),
      className: contactInfoIconClass,
      styles: {},
      src: 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6/svgs/solid/envelope.svg',
      attributes: {
        'data-element': 'contact-info-icon',
        'alt': 'Email Icon'
      }
    },
    
    // Email Text Line 1
    {
      id: emailTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Text Line 1',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {},
      children: [emailTextLine2Id],
      text: 'info@yourcompany.com',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Email Text Line 2
    {
      id: emailTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Text Line 2',
      parentId: emailTextId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {},
      children: [],
      text: 'support@yourcompany.com',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Form Wrapper
    {
      id: formWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Form Wrapper',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormWrapperClass,
      styles: {},
      children: [formId, successMessageId, errorMessageId],
      text: '',
      attributes: {
        'data-element': 'contact-form-wrapper'
      }
    },
    
    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Contact Form',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('form'),
      className: contactFormClass,
      styles: {},
      children: [nameGroupId, emailGroupId, messageGroupId, submitButtonId],
      method: 'post',
      action: '',
      text: '',
      attributes: {
        'data-element': 'contact-form',
        'data-form-type': 'contact'
      }
    },
    
    // Name Field Group
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Field Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Your Name',
      attributes: {
        'data-element': 'form-label',
        'for': 'contact-name'
      }
    },
    
    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('input'),
      className: contactFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'text',
        'id': 'contact-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': 'true'
      }
    },

    // Email Field Group
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Field Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Your Email',
      attributes: {
        'data-element': 'form-label',
        'for': 'contact-email'
      }
    },

    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('input'),
      className: contactFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'email',
        'id': 'contact-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': 'true'
      }
    },

    // Message Field Group
    {
      id: messageGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Message Field Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [messageLabelId, messageTextareaId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },

    // Message Label
    {
      id: messageLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Message Label',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Your Message',
      attributes: {
        'data-element': 'form-label',
        'for': 'contact-message'
      }
    },

    // Message Textarea
    {
      id: messageTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Message Textarea',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: contactFormTextareaClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-textarea',
        'id': 'contact-message',
        'name': 'message',
        'placeholder': 'Type your message here...',
        'required': 'true',
        'rows': '5'
      }
    },

    // Submit Button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: getHierarchyName('button'),
      className: contactFormButtonClass,
      styles: {},
      children: [],
      text: 'Send Message',
      attributes: {
        'data-element': 'form-button',
        'type': 'submit'
      }
    },

    // Success Message
    {
      id: successMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Success Message',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormSuccessClass,
      styles: {},
      children: [],
      text: 'Your message has been sent successfully! We\'ll get back to you soon.',
      attributes: {
        'data-element': 'form-success-message'
      }
    },

    // Error Message
    {
      id: errorMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Error Message',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormErrorClass,
      styles: {},
      children: [],
      text: 'There was an error sending your message. Please try again later.',
      attributes: {
        'data-element': 'form-error-message'
      }
    }
  ];
  
  return {
    contactSection: newElements[0],
    elements: newElements
  };
} 